/**
 * SpatiotemporalBlueNoise — the runtime side of the STBN mask and ONE per-pixel jitter node for ray marches, with the
 * four sequences the engine's marches have used side by side so a probe can compare them live (shader gaps program
 * round 3, agent F, 2026-09-30).
 *
 *   'white'   the shadowed light volume's pre-R3 hash of screen UV + time — white noise in space and time.
 *   'golden'  the engine's 2D void-and-cluster blue-noise tile (Ulichney 1993, `BlueNoiseGenerator`) with its VALUE
 *             advanced by the golden ratio every frame, v_t = fract(v + t·φ) — each pixel's sequence is low-discrepancy
 *             (the classic "animated blue noise", Wolfe 2017). The cost the STBN paper names: the slices lose some of
 *             their spatial blue, and the time axis carries a frequency peak (strobing).
 *   'ign'     interleaved gradient noise (Jimenez 2014) + frame·φ — the fire volume's pre-R3 jitter (built for TAA).
 *   'stbn'    the SCALAR SPATIOTEMPORAL BLUE NOISE mask (Wolfe et al., EGSR 2022): every slice is 2D blue noise AND
 *             every pixel's values over the 32 slices are 1D blue noise — `SpatiotemporalBlueNoiseGenerator`.
 *
 * SAMPLING (the paper / NVIDIA's blog): point-sampled, tiled over the screen at 1 texel per pixel, slice = frame mod
 * 32; two effects on one pixel read DIFFERENT texels — `jitterEffectOffset(effect)` shifts the tile by an R2 point
 * (× 64 texels) and the slice by a golden fraction, so the light volume (effect 1) and the fire (effect 2) never
 * share a sequence. Every candidate sample is `.toVar()`'d BEFORE the mode `select` (THE LAZY-SAMPLE LAW).
 *
 * THE DATA IS LAZY: `stbnTexture()` is created empty (all 0 = no jitter) and `loadSTBN()` fills it from a dynamic
 * import of `stbnData.ts` (a base64 module, 128 KB) — a build never loads it unless a caller asks for STBN. LICENCE /
 * PATENT: the mask is generated in this repository by the paper's algorithm (NVIDIA's released textures are NOT used —
 * their licence forbids open-source redistribution); the generation method is claimed by US 12,283,028 B2 (NVIDIA,
 * active) — see scripts/bake-stbn.mjs and Atlas's impl-volumes.md: an OWNER decision.
 */
import * as THREE from 'three/webgpu';
import { float, int, vec2, vec3, fract, dot, floor, select, texture, texture3D, screenCoordinate, screenUV, frameId, time, hash } from 'three/tsl';
import { getBlueNoiseTexture, initBlueNoise } from './BlueNoiseRuntime.js';

export const STBN_SIZE = 64;
export const STBN_SLICES = 32;
export const GOLDEN = 0.618033988749895;
/** R2 (Roberts 2018) — the plastic-number rank-1 lattice used to spread effect offsets over the tile. */
const R2_A1 = 0.7548776662466927, R2_A2 = 0.5698402909980532;

export type JitterMode = 'white' | 'golden' | 'ign' | 'stbn';
/** The integer each mode is selected by in the shader (a uniform, so a probe can switch live). */
export const JITTER_MODE_CODE: Readonly<Record<JitterMode, number>> = { white: 0, golden: 1, ign: 2, stbn: 3 };
export function jitterModeCode(m: JitterMode | string | undefined): number { return JITTER_MODE_CODE[(m ?? 'white') as JitterMode] ?? 0; }

/** An effect's decorrelating offset: [texel x, texel y, slice]. Effect 0 = no offset. */
export function jitterEffectOffset(effect: number): [number, number, number] {
  const e = Math.max(0, Math.floor(effect));
  const f = (v: number): number => v - Math.floor(v);
  return [Math.floor(f(e * R2_A1) * STBN_SIZE), Math.floor(f(e * R2_A2) * STBN_SIZE), Math.floor(f(e * GOLDEN) * STBN_SLICES)];
}

// ── The texture ─────────────────────────────────────────────────────────────────────────────────────────────────────
let _tex: THREE.Data3DTexture | null = null;
let _ready = false;
let _loading: Promise<boolean> | null = null;

/** The STBN 3D texture (64 × 64 × 32, R8, nearest, repeat). Empty (all zero) until `loadSTBN()` resolves. */
export function stbnTexture(): THREE.Data3DTexture {
  if (_tex) return _tex;
  const data = new Uint8Array(STBN_SIZE * STBN_SIZE * STBN_SLICES);
  const t = new THREE.Data3DTexture(data, STBN_SIZE, STBN_SIZE, STBN_SLICES);
  t.format = THREE.RedFormat;
  t.type = THREE.UnsignedByteType;
  t.colorSpace = THREE.NoColorSpace;
  t.minFilter = THREE.NearestFilter;
  t.magFilter = THREE.NearestFilter;
  t.wrapS = t.wrapT = t.wrapR = THREE.RepeatWrapping;
  t.generateMipmaps = false;
  t.unpackAlignment = 1;
  t.needsUpdate = true;
  t.name = 'STBN-64x64x32';
  _tex = t;
  return t;
}

/** True once the mask holds the baked data. */
export function isSTBNReady(): boolean { return _ready; }

/** Decode base64 without atob / Buffer (runs in a browser, a worker and node alike). */
export function decodeBase64(s: string): Uint8Array {
  const A = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  const lut = new Int16Array(128).fill(-1);
  for (let i = 0; i < A.length; i++) lut[A.charCodeAt(i)] = i;
  const clean = s.replace(/[^A-Za-z0-9+/]/g, '');
  const out = new Uint8Array(Math.floor((clean.length * 3) / 4));
  let o = 0, acc = 0, bits = 0;
  for (let i = 0; i < clean.length; i++) {
    acc = (acc << 6) | lut[clean.charCodeAt(i)];
    bits += 6;
    if (bits >= 8) { bits -= 8; out[o++] = (acc >> bits) & 0xff; }
  }
  return o === out.length ? out : out.subarray(0, o);
}

/** Load the baked mask into the texture (a dynamic import — once). Resolves true when ready. */
export function loadSTBN(): Promise<boolean> {
  if (_ready) return Promise.resolve(true);
  if (_loading) return _loading;
  _loading = import('./stbnData.js').then((m) => {
    const bytes = decodeBase64(m.STBN_DATA_B64);
    if (m.STBN_DATA_SIZE !== STBN_SIZE || m.STBN_DATA_SLICES !== STBN_SLICES || bytes.length !== STBN_SIZE * STBN_SIZE * STBN_SLICES) return false;
    const t = stbnTexture();
    (t.image.data as Uint8Array).set(bytes);
    t.needsUpdate = true;
    _ready = true;
    return true;
  }).catch(() => false);
  return _loading;
}

/** Test-only: forget the texture and the load. */
export function _resetSTBN(): void { _tex?.dispose(); _tex = null; _ready = false; _loading = null; }

// ── CPU mirrors (the harness judges the sequences with these) ───────────────────────────────────────────────────────
/** The golden mode at a pixel/frame from a 2D tile's byte (0..255). */
export function goldenJitterCpu(tileByte: number, frame: number): number { const v = (tileByte + 0.5) / 256 + frame * GOLDEN; return v - Math.floor(v); }
/** IGN + frame·φ (Jimenez 2014 — the fire's pre-R3 jitter). */
export function ignJitterCpu(px: number, py: number, frame: number): number {
  const f = (v: number): number => v - Math.floor(v);
  return f(f(52.9829189 * f(px * 0.06711056 + py * 0.00583715)) + frame * GOLDEN);
}
/** STBN at a pixel/frame for an effect, from the slice-major bytes. */
export function stbnJitterCpu(data: Uint8Array, px: number, py: number, frame: number, effect = 0): number {
  const [ox, oy, ot] = jitterEffectOffset(effect);
  const m = (v: number, n: number): number => ((v % n) + n) % n;
  const x = m(Math.floor(px) + ox, STBN_SIZE), y = m(Math.floor(py) + oy, STBN_SIZE), t = m(Math.floor(frame) + ot, STBN_SLICES);
  return (data[(t * STBN_SIZE + y) * STBN_SIZE + x] + 0.5) / 256;
}

// ── TSL ─────────────────────────────────────────────────────────────────────────────────────────────────────────────
type N = any;

/** The golden mode's 2D tile (the engine's void-and-cluster LUT; generated on first use — ~100 ms, or restored). */
function goldenTile(): THREE.Texture | null {
  if (!getBlueNoiseTexture()) initBlueNoise();
  return getBlueNoiseTexture();
}

/**
 * The per-pixel jitter in [0, 1) for this frame. `mode` is a JitterMode (one sequence compiled) or an int uniform
 * node (every candidate compiled, the uniform picks — live switching; each candidate is `.toVar()`'d before the
 * select). `effect` decorrelates two users on one pixel; `pixel` defaults to `screenCoordinate.xy` (the target's own
 * pixel grid — a half-resolution march jitters per half-resolution pixel).
 */
export function jitterNode(mode: JitterMode | N, effect = 0, pixel: N = screenCoordinate.xy): N {
  const [ox, oy, ot] = jitterEffectOffset(effect);
  const frame = float(frameId);
  const px = floor(vec2(pixel));
  const stbn = (): N => {
    const tex = stbnTexture();
    const cell = px.add(vec2(ox, oy)).mod(float(STBN_SIZE)).add(0.5).div(STBN_SIZE);
    const slice = frame.add(ot).mod(float(STBN_SLICES)).floor().add(0.5).div(STBN_SLICES);
    return texture3D(tex, vec3(cell, slice)).level(int(0)).r.mul(255 / 256).add(0.5 / 256);
  };
  const golden = (): N => {
    const tile = goldenTile();
    if (!tile) return fract(frame.mul(GOLDEN));
    const size = (tile.image as { width?: number }).width ?? 64;
    const v = texture(tile, px.add(vec2(ox, oy)).mod(float(size)).add(0.5).div(size)).level(int(0)).r.mul(255 / 256).add(0.5 / 256);
    return fract(v.add(frame.mul(GOLDEN)));
  };
  const ign = (): N => fract(fract(float(52.9829189).mul(fract(dot(px, vec2(0.06711056, 0.00583715))))).add(frame.mul(GOLDEN)));
  // The shadowed light volume's pre-R3 expression, verbatim (its legacy path keeps its own copy).
  const white = (): N => hash(screenUV.x.add(screenUV.y.mul(7.13)).mul(4096).add(time.fract().mul(31)));
  if (typeof mode === 'string') {
    return mode === 'stbn' ? stbn() : mode === 'golden' ? golden() : mode === 'ign' ? ign() : white();
  }
  const m = mode as N;
  const cS = stbn().toVar(), cG = golden().toVar(), cI = ign().toVar(), cW = white().toVar();
  return select(m.equal(3), cS, select(m.equal(2), cI, select(m.equal(1), cG, cW)));
}
